import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Image } from "react-bootstrap";
import Swal from "sweetalert2";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { get, post, put } from "../API/api_helper";

interface Props {
  show: boolean;
  onHide: () => void;
  student?: any | null;
  refresh: () => void;
}

const StudentModal: React.FC<Props> = ({ show, onHide, student, refresh }) => {
  const [formData, setFormData] = useState({
    name: "",
    admission_no: "",
    phone_code: "+266",
    phone: "",
    course: "",
    academic_period: "",
    blood_group: "",
    photo: null as File | null,
    grade: "",
    division: "",
    dob: "",
  });

  const [grades, setGrades] = useState<{ id: number; grade: string }[]>([]);
  const [divisions] = useState([1, 2, 3, 4, 5, 6]); // stored as numbers
  const divisionLetter = (num: number) => String.fromCharCode(64 + num);

  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>("");

  const [cropModal, setCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => { fetchGrades(); }, []);
  const fetchGrades = async () => {
    try {
      const res = await get("/grades");
      setGrades(res || []);
    } catch (error) { console.error("Failed to fetch grades:", error); }
  };

  const validatePhone = (code: string, phone: string) => {
    const cleaned = phone.replace(/\D/g, "");
    if (code === "+91" && cleaned.length !== 10) return "Indian phone numbers must be 10 digits.";
    if (code === "+27" && cleaned.length !== 9) return "South African phone numbers must be 9 digits.";
    if (code === "+266" && cleaned.length !== 8) return "Lesotho phone numbers must be 8 digits.";
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === "phone" || name === "phone_code") {
      const code = name === "phone_code" ? value : formData.phone_code;
      const phone = name === "phone" ? value : formData.phone;
      setPhoneError(validatePhone(code, phone));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({ icon: "error", title: "File too large", text: "Please upload an image ≤2MB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setImageSrc(reader.result as string); setCropModal(true); };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => { setCroppedAreaPixels(croppedAreaPixels); }, []);

  const getCroppedImage = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return null;
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    canvas.width = 150; canvas.height = 150;
    const { x, y, width, height } = croppedAreaPixels;
    ctx.drawImage(image, x, y, width, height, 0, 0, 150, 150);
    return new Promise<File | null>((resolve) => {
      canvas.toBlob(blob => {
        if (!blob) return resolve(null);
        const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
        setPreview(canvas.toDataURL("image/jpeg"));
        setFormData(prev => ({ ...prev, photo: file }));
        resolve(file);
      }, "image/jpeg");
    });
  }, [imageSrc, croppedAreaPixels]);

  const handleCropSave = async () => { await getCroppedImage(); setCropModal(false); };

  const resetForm = () => {
    setFormData({
      name: "", admission_no: "", phone_code: "+266", phone: "", course: "",
      academic_period: "", blood_group: "", photo: null, grade: "", division: "", dob: ""
    });
    setPreview(null); setPhoneError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationMessage = validatePhone(formData.phone_code, formData.phone);
    if (validationMessage) { Swal.fire({ icon: "error", title: "Invalid Phone Number", text: validationMessage }); return; }
    if (!formData.photo) { Swal.fire({ icon: "error", title: "Missing Photo", text: "Please upload a photo." }); return; }

    try {
      setLoading(true);
      let photoBase64: string | null = null;
      if (formData.photo instanceof File) {
        const reader = new FileReader();
        photoBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(formData.photo as Blob);
        });
      }

      const payload = { ...formData, photo: photoBase64 };
      if (student?.id) await put(`/students/${student.id}`, payload);
      else await post("/students", payload);

      Swal.fire({ icon: "success", title: "Student saved successfully!", timer: 1500, showConfirmButton: false });
      resetForm(); onHide(); refresh();
    } catch (error: any) {
      console.error("Backend error:", error.response?.data);
      Swal.fire({ icon: "error", title: "Failed to save student", text: error.response?.data?.message || "Check your input." });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (student) {
      setFormData(prev => ({
        ...prev,
        ...student,
        photo: prev.photo || student.photo || null,
        grade: student.grade || "",
        division: student.division || "",
        dob: student.dob || "",
      }));
      setPreview(prev => prev ?? (student.photo ? student.photo : null));
    }
  }, [student]);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{student ? "Edit Student" : "Add Student"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>

            {/* Photo */}
            <Form.Group controlId="photo" className="mb-3">
              <Form.Label>Student Photo <small className="text-muted">(150×150 px, ≤2MB)</small></Form.Label>
              <Row className="align-items-center">
                <Col md={8}>
                  <Form.Control type="file" name="photo" accept="image/*" onChange={handleFileChange} />
                </Col>
                <Col md={4} className="text-center">
                  {preview ? <Image src={preview} alt="Preview" thumbnail style={{ maxHeight: 120, width: 120, objectFit: "cover" }} /> :
                    <div className="border rounded d-inline-block" style={{ width: 120, height: 120, lineHeight: "120px", color: "#aaa", fontSize: "0.9rem", background: "#f8f9fa" }}>No Preview</div>}
                </Col>
              </Row>
            </Form.Group>

            {/* Name */}
            <Form.Group className="mb-3">
              <Form.Label>Student Name</Form.Label>
              <Form.Control type="text" name="name" placeholder="Enter student name" value={formData.name} onChange={handleChange} required />
            </Form.Group>

            {/* Admission No */}
            <Form.Group className="mb-3">
              <Form.Label>Admission No</Form.Label>
              <Form.Control type="text" name="admission_no" placeholder="Enter admission number" value={formData.admission_no} onChange={handleChange} required />
            </Form.Group>

            {/* DOB */}
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control type="date" name="dob" value={formData.dob} onChange={handleChange} />
            </Form.Group>

            {/* Grade & Division */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Grade</Form.Label>
                  <Form.Select name="grade" value={formData.grade} onChange={handleChange}>
                    <option value="">Select Grade</option>
                    {grades.map(g => <option key={g.id} value={g.id}>{g.grade}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Division</Form.Label>
                  <Form.Select name="division" value={formData.division} onChange={handleChange} required>
                    <option value="">Select Division</option>
                    {divisions.map(num => <option key={num} value={num}>{divisionLetter(num)}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Phone & Blood */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Phone Code</Form.Label>
                  <Form.Select name="phone_code" value={formData.phone_code} onChange={handleChange}>
                    <option value="+266">+266 (Lesotho)</option>
                    <option value="+91">+91 (India)</option>
                    <option value="+27">+27 (South Africa)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} isInvalid={!!phoneError} required />
                  {phoneError && <Form.Control.Feedback type="invalid">{phoneError}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Blood Group</Form.Label>
                  <Form.Select name="blood_group" value={formData.blood_group || ""} onChange={handleChange}>
                    <option value="">Select Blood Group</option>
                    {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Academic Period */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Academic Period</Form.Label>
                  <Form.Control type="text" name="academic_period" placeholder="e.g., 2024–2025" value={formData.academic_period} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button variant="secondary" onClick={onHide} className="me-2">Cancel</Button>
              <Button type="submit" variant="primary" disabled={loading}>{loading ? "Saving..." : "Save"}</Button>
            </div>

          </Form>
        </Modal.Body>
      </Modal>

      {/* Crop Modal */}
      <Modal show={cropModal} onHide={() => setCropModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Crop Image (150×150)</Modal.Title></Modal.Header>
        <Modal.Body style={{ position: "relative", height: 400 }}>
          {imageSrc && <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropSize={{ width: 150, height: 150 }}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            restrictPosition
          />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCropModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCropSave}>Save Crop</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StudentModal;

async function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = document.createElement("img");
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", error => reject(error));
    image.src = url;
  });
}
